import React, { useCallback, useEffect, useRef, useState } from 'react'
import tw, { styled, theme } from 'twin.macro'
import 'slick-carousel/slick/slick.css'
import 'react-image-lightbox/style.css'
import 'slick-carousel/slick/slick-theme.css'

import once from 'lodash/once'
import Slick from 'react-slick'
import Lightbox from 'react-image-lightbox'

import { Container as ArrowContainer, NextArrow, PrevArrow } from './Arrows'
import { imageUrl } from '@libs/appUrl'
import PopImage from '@components/PopImage'

const StyledPopImage = styled(PopImage)`
  width: 100%;
  padding-bottom: 52.3%;
`

const Container = styled.div`
  position: relative;
  ${tw`relative bg-gray-30`};
  line-height: 0;

  .slick-slider {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
  }

  .slick-list {
    transition: height 500ms cubic-bezier(0.02, 0.01, 0.47, 1);
    will-change: height;
  }

  ${ArrowContainer} {
    display: none;
  }

  @media (min-width: ${theme`screens.md`}) {
    &:hover ${ArrowContainer} {
      display: flex;
    }
  }
`

const Placeholder = styled.svg`
  height: 0px;
  padding-bottom: 52.3%;
  display: block;
`

const Pagination = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  padding: 16px;
  color: #fff;
`

type Image = {
  path: string
  w: number
  h: number
}

type CarouselProps = {
  images: Image[]
  editorType: string
  noShaper: boolean
}

// @refresh reset
function Carousel(props: CarouselProps) {
  const { images, editorType, noShaper } = props
  const containerRef = useRef<HTMLDivElement>(null)
  const placeholderRef = useRef(null)
  const slickRef = useRef(null)
  const [index, setIndex] = useState(0)
  const DESKTOP_IMAGE_WIDTH = 780
  const BIG_TABLET_IMAGE_WIDTH = 650
  const SMALL_TABLET_IMAGE_WIDTH = 768

  const {
    photoIndex,
    nextPhotoIndex,
    prevPhotoIndex,
    onMoveNextRequest,
    onMovePrevRequest,
    setPhotoIndex,
  } = useLightbox(images, slickRef)

  const removePlaceholder = useCallback(
    once(() => {
      const container = containerRef.current
      placeholderRef.current.style.display = 'none'
      container.querySelector<HTMLDivElement>('.slick-slider').style.position = 'relative'
    }),
    [],
  )

  useEffect(() => {
    slickRef.current.slickGoTo(0, false)
  }, [])

  const toReactChildren = (image, index) => {
    const { path } = image

    return (
      <StyledPopImage
        showOriginal={noShaper}
        imageUrl={imageUrl(path)}
        sizes={`(min-width: 1216px) ${DESKTOP_IMAGE_WIDTH}px, (min-width: 992px) ${BIG_TABLET_IMAGE_WIDTH}px, (min-width: 768px) ${SMALL_TABLET_IMAGE_WIDTH}px, calc(100vw - 30px)`}
        key={index}
        onClick={() => setPhotoIndex(index)}
        ratio={0.52}
      />
    )
  }

  return (
    <Container ref={containerRef}>
      <Placeholder ref={placeholderRef} />
      <Slick
        ref={slickRef}
        prevArrow={<PrevArrow />}
        nextArrow={<NextArrow />}
        adaptiveHeight={true}
        afterChange={(nextIndex) => setIndex(nextIndex)}
        infinite={false}
        beforeChange={removePlaceholder}
      >
        {images.map(toReactChildren)}
      </Slick>
      <Pagination>
        {index + 1} / {images.length}
      </Pagination>
      {photoIndex !== undefined && (
        <Lightbox
          mainSrc={imageUrl(images[photoIndex].path)}
          prevSrc={imageUrl(images[prevPhotoIndex].path)}
          nextSrc={imageUrl(images[nextPhotoIndex].path)}
          onCloseRequest={() => setPhotoIndex(undefined)}
          onMovePrevRequest={onMovePrevRequest}
          onMoveNextRequest={onMoveNextRequest}
        />
      )}
    </Container>
  )
}

const useLightbox = (images: Image[], slickRef: React.MutableRefObject<any>) => {
  const [photoIndex, setPhotoIndex] = useState<number | undefined>(undefined)

  const nextPhotoIndex = photoIndex !== undefined && (photoIndex + 1) % images.length
  const prevPhotoIndex =
    photoIndex !== undefined && (photoIndex + images.length - 1) % images.length

  const onMoveNextRequest = () => {
    setPhotoIndex(nextPhotoIndex)
    slickRef.current.slickGoTo(nextPhotoIndex, false)
  }

  const onMovePrevRequest = () => {
    setPhotoIndex(prevPhotoIndex)
    slickRef.current.slickGoTo(prevPhotoIndex, false)
  }

  return {
    photoIndex,
    nextPhotoIndex,
    prevPhotoIndex,
    onMoveNextRequest,
    onMovePrevRequest,
    setPhotoIndex,
  }
}
export default Carousel
